import React, { useState, useEffect, useMemo } from "react";
import { useResources } from "../../hooks/useResources";
import { useSubjectsStore } from "../../stores/subjects.store";
import { useLanguagesStore } from "../../stores/languages.store";
import { useGradesStore } from "../../stores/grades.store";
import { useSectionsStore } from "../../stores/sections.store";
import CustomFormModal from "./CustomFormModal";
import CustomSelectDropdown from "./CustomSelectDropdown";
import type { SelectOption } from "../../interfaces/componentInterfaces/dropdown";
import "./AddSectionModal.scss";

export interface AddSectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const AddSectionModal: React.FC<AddSectionModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { getResourceValue } = useResources();
  const { subjects, allSubjects, load: loadSubjects, filterByGradeAndLanguage, getAvailableLanguagesForGrade } = useSubjectsStore();
  const { languages, load: loadLanguages } = useLanguagesStore();
  const { grades, load: loadGrades, filterByCountry } = useGradesStore();
  const { createSection } = useSectionsStore();

  const [formData, setFormData] = useState({
    title: "",
    country: "",
    subject: "",
    language: "",
    grade: "",
    term: "",
  });

  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Initialize form when modal opens
  useEffect(() => {
    if (isOpen) {
      loadLanguages();
      loadGrades();
      // Reset form when modal opens
      setFormData({
        title: "",
        country: "",
        subject: "",
        language: "",
        grade: "",
        term: "",
      });
      setError(null);
    }
  }, [isOpen, loadLanguages, loadGrades]);

  useEffect(() => {
    if (formData.country) {
      filterByCountry(formData.country);
    } else {
      filterByCountry(null);
    }
  }, [formData.country, filterByCountry]);

  // Filter grades to Egypt (country_iso_code = 'eg') when grades are loaded and modal is open
  useEffect(() => {
    if (isOpen && grades.length > 0) {
      filterByCountry('eg');
    }
  }, [isOpen, grades.length, filterByCountry]);

  useEffect(() => {
    const loadAndFilterSubjects = async () => {
      if (formData.grade && formData.language) {
        // Load subjects first, then filter
        await loadSubjects(formData.grade);
        filterByGradeAndLanguage(formData.grade, formData.language);
      } else if (formData.grade) {
        // Load subjects for the grade
        await loadSubjects(formData.grade);
        filterByGradeAndLanguage(formData.grade, null);
      } else {
        // No grade selected, load all subjects
        await loadSubjects();
        filterByGradeAndLanguage(null, null);
      }
    };

    loadAndFilterSubjects();
  }, [formData.grade, formData.language, loadSubjects, filterByGradeAndLanguage]);

  useEffect(() => {
    setFormData((prev) => ({ ...prev, language: "", subject: "", term: "" }));
  }, [formData.grade]);

  // Reset subject when language changes
  useEffect(() => {
    setFormData((prev) => ({ ...prev, subject: "", term: "" }));
  }, [formData.language]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError(null);
  };

  const handleSelectChange = (name: string) => (value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.title.trim()) {
      setError(getResourceValue("title_required", "Title is required"));
      return;
    }

    if (!formData.subject) {
      setError(getResourceValue("subject_required", "Please select a subject"));
      return;
    }

    if (!formData.language) {
      setError(getResourceValue("language_required", "Please select a language"));
      return;
    }

    if (!formData.grade) {
      setError(getResourceValue("grade_required", "Please select a grade"));
      return;
    }

    if (!formData.term) {
      setError(getResourceValue("term_required", "Please select a term"));
      return;
    }

    setIsLoading(true);
    try {
      await createSection({
        subject_id: formData.subject,
        term_code: formData.term,
        section_title: formData.title.trim(),
        section_is_original: true,
        section_original_section_id: null,
        section_language_iso_code: formData.language.toUpperCase(),
      });

      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (err: any) {
      setError(err?.message || getResourceValue("add_section_failed", "Failed to add section"));
    } finally {
      setIsLoading(false);
    }
  };

  const gradeOptions: SelectOption[] = [
    { value: "", label: getResourceValue("select_grade", "Select Grade") },
    ...(grades || []).map((grade) => ({
      value: grade.grade_id,
      label: getResourceValue(grade.grade_localization_key, grade.grade_title),
    })),
  ];

  // Filter language options based on selected grade
  const languageOptions: SelectOption[] = useMemo(() => {
    const baseOptions: SelectOption[] = [
      { value: "", label: getResourceValue("select_language", "Select Language") },
    ];

    // If no grade is selected, only show the placeholder
    if (!formData.grade) {
      return baseOptions;
    }

    // If languages haven't loaded yet, only show placeholder
    if (!languages || languages.length === 0) {
      return baseOptions;
    }

    // If subjects haven't loaded yet, only show placeholder
    if (!allSubjects || allSubjects.length === 0) {
      return baseOptions;
    }

    // Get available languages for the selected grade
    const availableLanguageCodes = getAvailableLanguagesForGrade(formData.grade);

    // If no languages available for this grade, show placeholder only
    if (availableLanguageCodes.length === 0) {
      return baseOptions;
    }

    const availableLanguageCodesLower = availableLanguageCodes.map((code) => code.toLowerCase());

    // Filter languages to only include those available for the selected grade
    const filteredLanguages = languages.filter((language) =>
      availableLanguageCodesLower.includes(language.language_iso_code.toLowerCase())
    );

    return [
      ...baseOptions,
      ...filteredLanguages.map((language) => ({
        value: language.language_iso_code.toLowerCase(),
        label: language.language_english_name,
      })),
    ];
  }, [formData.grade, languages, allSubjects, getAvailableLanguagesForGrade, getResourceValue]);

  // Filter subject options based on selected grade and language
  const subjectOptions: SelectOption[] = useMemo(() => {
    const baseOptions: SelectOption[] = [
      { value: "", label: getResourceValue("select_subject", "Select Subject") },
    ];

    // If no grade is selected, only show the placeholder
    if (!formData.grade) {
      return baseOptions;
    }

    // If no language is selected, only show the placeholder
    if (!formData.language) {
      return baseOptions;
    }

    // Show subjects filtered by grade and language
    return [
      ...baseOptions,
      ...(subjects || []).map((subject) => ({
        value: subject.subject_id,
        label: subject.subject_title,
      })),
    ];
  }, [formData.grade, formData.language, subjects, getResourceValue]);

  const termOptions: SelectOption[] = [
    { value: "", label: getResourceValue("select_term", "Select Term") },
    { value: "t0", label: getResourceValue("term_0", "Term 0") },
    { value: "t1", label: getResourceValue("term_1", "Term 1") },
    { value: "t2", label: getResourceValue("term_2", "Term 2") },
  ];

  // Check if all required fields are filled
  const isFormValid = useMemo(() => {
    return (
      formData.title.trim() !== "" &&
      formData.grade !== "" &&
      formData.language !== "" &&
      formData.subject !== "" &&
      formData.term !== ""
    );
  }, [formData.title, formData.grade, formData.language, formData.subject, formData.term]);

  return (
    <CustomFormModal
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={handleSubmit}
      title={getResourceValue("add_section", "Add Section")}
      submitButtonText={getResourceValue("add_section", "Add Section")}
      isLoading={isLoading}
      error={error}
      isSubmitDisabled={!isFormValid}
    >
      <div className="add-section-modal__field">
        <input
          type="text"
          name="title"
          placeholder={getResourceValue("title", "Title")}
          value={formData.title}
          onChange={handleInputChange}
          autoComplete="off"
          className="add-section-modal__input"
        />
      </div>

      <div className="add-section-modal__field">
        <CustomSelectDropdown
          id="grade-select"
          options={gradeOptions}
          value={formData.grade}
          onChange={handleSelectChange("grade")}
          placeholder={getResourceValue("select_grade", "Select Grade")}
        />
      </div>

      <div className="add-section-modal__field">
        <CustomSelectDropdown
          id="language-select"
          options={languageOptions}
          value={formData.language}
          onChange={handleSelectChange("language")}
          placeholder={getResourceValue("select_language", "Select Language")}
        />
      </div>

      <div className="add-section-modal__field">
        <CustomSelectDropdown
          id="subject-select"
          options={subjectOptions}
          value={formData.subject}
          onChange={handleSelectChange("subject")}
          placeholder={getResourceValue("select_subject", "Select Subject")}
        />
      </div>

      <div className="add-section-modal__field">
        <CustomSelectDropdown
          id="term-select"
          options={termOptions}
          value={formData.term}
          onChange={handleSelectChange("term")}
          placeholder={getResourceValue("select_term", "Select Term")}
        />
      </div>
    </CustomFormModal>
  );
};

export default AddSectionModal;
